/**
 * Reintento de un comando de pluma cuando el controlador está ocupado.
 *
 * El bridge serializa TODA llamada al SDK con un solo semáforo, y la puerta
 * espera turno sólo 8 s (DoorAcquireTimeout en hikvision-sdk-bridge/Program.cs:
 * más largo arriesgaba una pluma que se abre sola cuando ya nadie la espera).
 * Las búsquedas de historial del sync tardan 45-90 s y corren cada ~2 min, así
 * que una apertura cae en medio de una cerca de la mitad de las veces. Medido
 * en Acuapue el 14-oct-2026: la entrada de las 19:40:01 UTC abrió en 1 s y la
 * salida de las 19:40:44 falló con "SDK busy" porque la búsqueda de prod corría
 * de 19:40:18 a 19:41:09.
 *
 * El reintento es del front y no del relay ni de la nube: no hay que reinstalar
 * nada en la PC del sitio ni desplegar funciones. El de fondo es #765 (búsqueda
 * de 2 días cada 15 min con el vivo sano) o un gate con prioridad de puerta en
 * el bridge. Quien llama pasa un AbortSignal y lo aborta al desmontar: si el
 * operador se va, no queda una pluma abriéndose sola.
 */

/**
 * El bridge contesta `SDK busy (another <x> is in flight) — retry` cuando no
 * consigue turno. `<x>` es la etiqueta del PROPIO comando ("door open"), no lo
 * que ocupa el semáforo — por eso el aviso al operador no nombra la causa.
 */
export const isControllerBusy = (message: string | null | undefined): boolean =>
  typeof message === "string" && /SDK busy/i.test(message);

/**
 * 120 s: cubre la búsqueda más larga medida (93 s, Acuapue 14-oct 19:26-19:27)
 * más un intento. Cada intento son ~3 s hasta que el relay lo toma + hasta 8 s
 * esperando el semáforo, así que caben ~10.
 */
export const DOOR_BUSY_RETRY_WINDOW_MS = 120_000;

/**
 * Lo que puede durar un intento: 1,5 s de pausa + 3 s de poll del relay + 8 s
 * de semáforo. No se arranca uno que no termine dentro de la ventana, para que
 * los 120 s sean un tope real y no "120 s más lo que dure el último".
 */
export const DOOR_ATTEMPT_BUDGET_MS = 12_500;

/**
 * ¿Se manda otro intento? Sólo si el anterior FALLÓ por ocupado — nunca si quedó
 * pendiente o en curso: ése todavía puede ejecutarse, y un segundo comando
 * abriría la pluma dos veces.
 */
export const shouldRetryDoorCommand = (
  last: { status: string; message?: string | null },
  startedAtMs: number,
  nowMs: number,
  windowMs = DOOR_BUSY_RETRY_WINDOW_MS,
): boolean =>
  last.status === "failed" &&
  isControllerBusy(last.message) &&
  nowMs - startedAtMs + DOOR_ATTEMPT_BUDGET_MS <= windowMs;
